import { useQuery } from "@tanstack/react-query";
import { sapienAxios } from "@/inertia-utils/hooks";
import { useGetContentBlockWithChildren } from "@/Pages/DesignStudio/Design/useContentBlockState";
import {
    ContentBlockShape,
    ContentBlockType,
    ModelVariableUnit,
    VariableValue,
} from "@/models";
import { useFormatVariableValue } from "@/hooks";
 import { ThemeObject } from "@/styles";
import { LaravelRequestBodyShapes } from "@/ziggy-shims";
import { flatten } from "@/util";

type ContentBlockVariableValue = VariableValue & {
    label: string;
    unit: ModelVariableUnit;
    is_integer: boolean;
    theme: ThemeObject;
};

const recursiveGetContentBlockModelDataSources = (
    contentBlock: ContentBlockShape,
    acc: string[],
): string[] => {
    if (contentBlock.contentBlockModelDataSources?.length) {
        return [
            ...acc,
            ...contentBlock.contentBlockModelDataSources.map(
                (source) => source.id,
            ),
        ];
    } else if (contentBlock.contentBlocks?.length) {
        return contentBlock.contentBlocks.reduce((acc, child) => {
            return recursiveGetContentBlockModelDataSources(child, acc);
        }, acc);
    }
    return acc;
};

async function getValuesFromModelDataSources(
    content_block_model_data_source_ids: string[],
) {
    const response = await sapienAxios.post<
        {
            values: { [index: string]: ContentBlockVariableValue[] };
        },
        "get-values-from-model-data-sources"
    >("get-values-from-model-data-sources", {
        content_block_model_data_source_ids,
    } as unknown as (typeof LaravelRequestBodyShapes)["get-values-from-model-data-sources"]);
    return response.data;
}

export const useGetContentBlocksFromTableValues = (contentBlockId: string) => {
    const withChildren = useGetContentBlockWithChildren(contentBlockId);
    const formatVariableValue = useFormatVariableValue();

    const { data, isPending } = useQuery({
        queryKey: ["chartValues", contentBlockId],
        queryFn: async (): Promise<{
            contentBlocks: ContentBlockShape[];
        }> => {
            if (
                withChildren?.contentBlocks?.length > 0 &&
                withChildren.contentBlocks[0]?.contentBlocks?.length > 0
            ) {
                return {
                    contentBlocks: withChildren.contentBlocks,
                };
            }

            const contentBlockModelDataSourceIds =
                recursiveGetContentBlockModelDataSources(withChildren, []);



            const { values } = await getValuesFromModelDataSources(
                contentBlockModelDataSourceIds,
            );

            const firstRowLength = values[Object.keys(values)[0]].length + 1;

            const tableHeadContentBlock = withChildren?.contentBlocks?.find(
                (block) => block.content_block_type === ContentBlockType["Table Head"],
            );
            const tableHeadBlock: ContentBlockShape = {
                id: null,
                content_block_type: ContentBlockType["Table Head"],
                contentBlocks: [
                    {
                        id: null,
                        content: "",
                        content_block_type: ContentBlockType["Table Row"],
                        contentBlocks: Array.from(
                            { length: firstRowLength },
                            (_, index) => {
                                const content =
                                    index === 0
                                        ? "Time Horizon"
                                        : index.toString();
                                return {
                                    id: null,
                                    content: content,
                                    content_block_type:
                                        ContentBlockType["Table Header Cell"],
                                    contentBlocks: [],
                                    theme: {
                                    },
                                };
                            },
                        ),
                    },
                ],
            };

            const rows: ContentBlockShape[] = flatten(
                withChildren?.contentBlocks,
                "contentBlocks",
                "parent_content_block_id",
            ).filter(
                (row: ContentBlockShape) =>
                    row.content_block_type === ContentBlockType["Table Row"],
            );

            const tableBodyBlock: ContentBlockShape = {
                id: null,
                content_block_type: ContentBlockType["Table Body"],
                contentBlocks: Object.keys(values).map((key) => {
                    const correspondingTableRow = rows.find((row) => {
                        return row.contentBlockModelDataSources?.some(
                            function (source) {
                                const value = values[key]?.[0]
                                return source.model_variable_id === value?.model_variable_id;
                            },
                        );
                    });
                    return {
                        id: correspondingTableRow?.id,
                        content: "something",
                        content_block_type: ContentBlockType["Table Row"],
                        contentBlocks: [
                            {
                                id: null,
                                content: values[key][0].label,
                                content_block_type:
                                    ContentBlockType["Table Data Cell"],
                                contentBlocks: [],
                                theme: {
                                    ...correspondingTableRow?.theme,
                                },
                            },
                            ...values[key].map((value) => {
                                const content = formatVariableValue(
                                    value.unit,
                                    value.numerical_value,
                                    value.is_integer,
                                );

                                return {
                                    id: null,
                                    content,
                                    content_block_type:
                                        ContentBlockType["Table Data Cell"],
                                    contentBlocks: [],
                                    theme:{}
                                };
                            }),
                        ],
                    };
                }),
            };

            return {
                contentBlocks: [tableHeadBlock, tableBodyBlock],
            };
        },
        initialData: { contentBlocks: [] as ContentBlockShape[] },
    });

    return {
        contentBlocks: data?.contentBlocks,
        isPending,
    };
};
